import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ProjectionValue } from '@/components/px/ProjectionValue'
import { SectionLabel } from '@/components/px/SectionLabel'
import { projectNav } from '@/components/px/nav'
import {
  fetchMeteringUsage,
  fetchRunBudget,
  meteringErrorDismissed,
} from '@/features/metering/slices/meteringSlice'
import {
  selectMeteringByAgent,
  selectMeteringDaily,
  selectMeteringError,
  selectMeteringUsage,
  selectMeteringUsageLoading,
  selectRunBudget,
  selectRunBudgetLoading,
  selectRunBudgetMissing,
} from '@/features/metering/selectors'
import type { MeteringUsageShape } from '@/features/metering/types'
import { formatCount, formatDay, formatMinutes, formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * AGENT SPEND (`/project/:projectId/agent-spend`, PX-W6.4 AC-5): what the
 * project's AI runs consumed — the usage projection in its three shapes
 * (summary / daily / by-agent) and the run-budget remainder.
 *
 * Tokens and minutes are client-visible facts; the cost fields are FIRM-side
 * and go through `ProjectionValue` (absent key → "not shared", never zero).
 * `llmSplit` renders as a token/minute split, never cost. A 404 on the run
 * budget is the explicit "no run budget agreed" fact (I-7); a negative
 * `remainingMinor` is an overrun, rendered unclamped.
 */

const SHAPES: readonly MeteringUsageShape[] = ['summary', 'daily', 'by-agent']

/** Explicit label key per shape — no dynamic key interpolation. */
const SHAPE_LABEL_KEYS: Record<MeteringUsageShape, string> = {
  summary: 'commercial:agentSpend.shapes.summary',
  daily: 'commercial:agentSpend.shapes.daily',
  'by-agent': 'commercial:agentSpend.shapes.byAgent'
}

export default function AgentSpendPage() {
  const { t } = useTranslation('commercial')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()

  const [what, setWhat] = useState<MeteringUsageShape>('summary')

  const usage = useAppSelector(selectMeteringUsage)
  const usageLoading = useAppSelector(selectMeteringUsageLoading)
  const error = useAppSelector(selectMeteringError)
  const daily = useAppSelector(selectMeteringDaily)
  const byAgent = useAppSelector(selectMeteringByAgent)
  const remaining = useAppSelector(selectRunBudget)
  const remainingMissing = useAppSelector(selectRunBudgetMissing)
  const remainingLoading = useAppSelector(selectRunBudgetLoading)

  useEffect(() => {
    if (projectId === undefined) return
    void dispatch(fetchMeteringUsage({ projectId, what }))
  }, [dispatch, projectId, what])

  useEffect(() => {
    if (projectId === undefined) return
    void dispatch(fetchRunBudget(projectId))
  }, [dispatch, projectId])

  const onRetryClicked = (): void => {
    if (projectId === undefined) return
    dispatch(meteringErrorDismissed())
    void dispatch(fetchMeteringUsage({ projectId, what }))
    void dispatch(fetchRunBudget(projectId))
  }

  // PX-W6.6: the project rail follows the ROUTE, not a demo hero.
  const rail = { id: projectId ?? '', client: usage?.projectSlug ?? projectId ?? '' }
  const currency = usage?.currency

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.project')}
      breadcrumb={<span>{t('commercial:agentSpend.title')}</span>}
      contextPill={(usage?.projectSlug ?? projectId ?? '—').toUpperCase()}
      nav={projectNav(rail)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('commercial:agentSpend.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('commercial:agentSpend.lede')}</p>
      </header>

      <div className="mt-5 space-y-4">
        {/* ── The run budget, or the explicit no-run-budget fact ─────────── */}
        <section aria-label={t('commercial:agentSpend.runBudget.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
          <SectionLabel>{t('commercial:agentSpend.runBudget.title')}</SectionLabel>
          {remainingLoading ? (
            <LoadingPanel rows={1} className="border-0" />
          ) : remainingMissing ? (
            <div className="mt-2">
              <EmptyPanel
                title={t('commercial:agentSpend.runBudget.none')}
                body={t('commercial:agentSpend.runBudget.noneBody')}
              />
            </div>
          ) : remaining === null ? (
            <p className="mt-2 text-sm text-muted-foreground">—</p>
          ) : (
            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm lg:grid-cols-4">
              <div>
                <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:agentSpend.runBudget.agreed')}
                </dt>
                <dd className="mt-0.5 text-foreground">
                  <ProjectionValue
                    value={remaining.amountMinor}
                    format={(n) => formatMoney(n, remaining.currency)}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:agentSpend.runBudget.spent')}
                </dt>
                <dd className="mt-0.5 text-foreground">
                  <ProjectionValue
                    value={remaining.spentMinor}
                    format={(n) => formatMoney(n, remaining.currency)}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:agentSpend.runBudget.remaining')}
                </dt>
                <dd
                  className={cn(
                    'mt-0.5 font-semibold tabular-nums',
                    remaining.remainingMinor < 0 ? 'text-blocked' : 'text-foreground'
                  )}
                >
                  <ProjectionValue
                    value={remaining.remainingMinor}
                    format={(n) => formatMoney(n, remaining.currency)}
                  />
                  {remaining.remainingMinor < 0 && (
                    <span className="ml-2 rounded-full bg-blocked-bg px-2 py-0.5 text-[0.625rem] font-medium text-blocked">
                      {t('commercial:common.overrun')}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:agentSpend.runBudget.excluded')}
                </dt>
                <dd className="mt-0.5 text-foreground">{remaining.excludedEventCount}</dd>
              </div>
            </dl>
          )}
        </section>

        {/* ── Usage — one shape at a time, on demand ─────────────────────── */}
        <section aria-label={t('commercial:agentSpend.usage.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>{t('commercial:agentSpend.usage.title')}</SectionLabel>
            <div role="tablist" aria-label={t('commercial:agentSpend.usage.shape')} className="flex gap-1">
              {SHAPES.map((shape) => (
                <button
                  key={shape}
                  type="button"
                  role="tab"
                  aria-selected={what === shape}
                  onClick={() => setWhat(shape)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium',
                    what === shape
                      ? 'bg-accent text-accent-foreground'
                      : 'border border-input text-muted-foreground hover:bg-muted'
                  )}
                >
                  {t(SHAPE_LABEL_KEYS[shape])}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-2">
            {usageLoading ? (
              <LoadingPanel />
            ) : error !== null ? (
              <ErrorPanel
                error={{ status: error.status ?? 0, message: error.message, backendKey: error.key }}
                onRetryClicked={onRetryClicked}
              />
            ) : usage === null ? (
              <EmptyPanel
                title={t('commercial:agentSpend.empty.title')}
                body={t('commercial:agentSpend.empty.body')}
              />
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm lg:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.tokensIn')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{formatCount(usage.totalTokensIn)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.tokensOut')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{formatCount(usage.totalTokensOut)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.minutes')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{formatMinutes(usage.totalMinutes)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.events')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">{usage.eventCount}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.cost')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">
                      <ProjectionValue
                        value={usage.totalCostMinor}
                        format={(n) => formatMoney(n, currency)}
                      />
                    </dd>
                  </div>
                  <div className="lg:col-span-2">
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:agentSpend.usage.split')}
                    </dt>
                    {/* The split is tokens and minutes only — never cost. */}
                    <dd className="mt-0.5 text-foreground">
                      {t('commercial:agentSpend.usage.platform', {
                        minutes: formatMinutes(usage.llmSplit.platformMinutes),
                        tokens: formatCount(usage.llmSplit.platformTokens)
                      })}{' '}
                      ·{' '}
                      {t('commercial:agentSpend.usage.byok', {
                        minutes: formatMinutes(usage.llmSplit.byokMinutes),
                        tokens: formatCount(usage.llmSplit.byokTokens)
                      })}
                    </dd>
                  </div>
                </dl>

                {what === 'daily' && (
                  <div className="mt-4 overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="v-row border-b border-border bg-secondary">
                          <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.daily.day')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.daily.tokens')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.daily.minutes')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.daily.events')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.daily.cost')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {daily.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">
                              {t('commercial:agentSpend.empty.body')}
                            </td>
                          </tr>
                        ) : (
                          daily.map((point) => (
                            <tr key={point.date} className="v-row border-b border-border last:border-b-0">
                              <td className="px-3 py-2.5 text-foreground">{formatDay(point.date)}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                                {formatCount(point.tokensIn + point.tokensOut)}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                                {formatMinutes(point.minutes)}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                                {point.eventCount}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <ProjectionValue
                                  value={point.costMinor}
                                  format={(n) => formatMoney(n, currency)}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {what === 'by-agent' && (
                  <div className="mt-4 overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="v-row border-b border-border bg-secondary">
                          <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.byAgent.agent')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.byAgent.phase')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.byAgent.tokens')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.byAgent.minutes')}
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {t('commercial:agentSpend.byAgent.cost')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {byAgent.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">
                              {t('commercial:agentSpend.empty.body')}
                            </td>
                          </tr>
                        ) : (
                          byAgent.map((row) => (
                            <tr key={row.agentKey} className="v-row border-b border-border last:border-b-0">
                              <td className="px-3 py-2.5 font-medium text-foreground">{row.agentKey}</td>
                              <td className="px-3 py-2.5 text-muted-foreground">{row.phase ?? '—'}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                                {formatCount(row.tokensIn + row.tokensOut)}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                                {formatMinutes(row.minutes)}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <ProjectionValue
                                  value={row.costMinor}
                                  format={(n) => formatMoney(n, currency)}
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  )
}
